import React, { useState } from 'react';
import { Check } from 'lucide-react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import axiosInstance from 'shared/services/api/axiosInstance';
import { trodoAnalyticsTheme as theme } from 'features/analytics/studio/theme';

/**
 * ChatAnswerFeedback — 👍 / 👎 under an assistant answer.
 *
 * A thumbs-down is the Teach button: the backend records it on the agent run
 * (so the chat's own evaluators see it) and distils a durable lesson into team
 * memory that is recalled on future questions. We ask for an optional "what was
 * wrong?" because a specific correction produces a far better lesson than an
 * inferred one — but we never block on it.
 */
export default function ChatAnswerFeedback({ sessionId, messageId, teamId, initial = null }) {
  // `initial` is what the server stored on the message, so a reload shows it.
  const [sent, setSent] = useState(initial === 'good' || initial === 'bad' ? initial : null); // 'good' | 'bad'
  const [askWhy, setAskWhy] = useState(false);
  const restored = Boolean(sent) && sent === initial;
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [learned, setLearned] = useState(false);
  const [error, setError] = useState('');

  if (!sessionId || !messageId) return null;

  const submit = async (satisfaction, text) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post(
        '/chat/feedback',
        { sessionId, messageId, satisfaction, comment: text || undefined },
        { headers: { 'X-Team-Id': String(teamId) } },
      );
      setSent(satisfaction);
      setAskWhy(false);
      setLearned(Boolean(data?.learned));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not send feedback');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" style={{ color: theme.textTertiary }}>
        <Check size={12} />
        {restored
          ? (sent === 'good' ? 'You rated this helpful.' : 'You rated this unhelpful.')
          : sent === 'good'
          ? 'Thanks: noted.'
          : learned
            ? 'Thanks: I\'ve learned from this and will apply it next time.'
            : 'Thanks: feedback recorded.'}
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {!askWhy ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => submit('good')}
            aria-label="Helpful"
            title="Helpful"
            className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-surface-hover disabled:opacity-40"
            style={{ color: theme.textTertiary }}
          >
            <ThumbIcon direction="up" size={13} />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setAskWhy(true)}
            aria-label="Not helpful: teach me"
            title="Not helpful: tell me what was wrong and I'll learn from it"
            className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-surface-hover disabled:opacity-40"
            style={{ color: theme.textTertiary }}
          >
            <ThumbIcon direction="down" size={13} />
          </button>
          {error ? <span className="ml-1 text-[11px]" style={{ color: 'rgb(239,68,68)' }}>{error}</span> : null}
        </div>
      ) : (
        <div className="rounded-lg border p-2" style={{ borderColor: theme.border }}>
          <div className="mb-1.5 text-[11px]" style={{ color: theme.textSecondary }}>
            What was wrong? I&apos;ll turn it into a rule I apply from now on.
          </div>
          <textarea
            rows={2}
            autoFocus
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit('bad', comment); }
              if (e.key === 'Escape') setAskWhy(false);
            }}
            placeholder="e.g. use a 90-day window by default, not 30"
            className="w-full resize-none rounded-md border px-2 py-1 text-[12px]"
            style={{ borderColor: theme.border, background: 'transparent', color: theme.textPrimary }}
          />
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => submit('bad', comment)}
              className="rounded-md bg-brand px-2 py-1 text-[11px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50"
            >
              {busy ? 'Teaching…' : 'Teach'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => submit('bad')}
              className="rounded-md px-2 py-1 text-[11px] disabled:opacity-50"
              style={{ color: theme.textSecondary }}
            >
              Just mark unhelpful
            </button>
            <button
              type="button"
              onClick={() => setAskWhy(false)}
              className="ml-auto rounded-md px-2 py-1 text-[11px]"
              style={{ color: theme.textTertiary }}
            >
              Cancel
            </button>
          </div>
          {error ? <div className="mt-1 text-[11px]" style={{ color: 'rgb(239,68,68)' }}>{error}</div> : null}
        </div>
      )}
    </div>
  );
}
